import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { FiAlertTriangle, FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";

const NotFound = ({ backTo, backLabel }) => {
  const { t, i18n } = useTranslation("app_pages");
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  const isAr = i18n.language === 'ar' || i18n.language?.startsWith('ar');
  const BackIcon = isAr ? FiArrowRight : FiArrowLeft;

  const role = (user?.role || user?.roles?.[0])?.toLowerCase();
  const isAdmin = role === 'admin' || role === 'superadmin' || user?.is_superuser;

  const getResolvedDestination = () => {
    if (backTo) return backTo;
    if (!isAuthenticated) return '/login';

    const currentPath = location.pathname || '';
    if (isAdmin || currentPath.startsWith('/admin')) {
      return '/admin';
    }
    if (currentPath.startsWith('/school') || role === 'teacher' || role === 'instructor') {
      return '/school';
    }
    if (currentPath.startsWith('/teen')) {
      return currentPath.includes('/viedma') ? '/teen/viedma' : '/teen';
    }
    if (currentPath.startsWith('/kids')) {
      return currentPath.includes('/viedma') ? '/kids/viedma' : '/kids';
    }
    if (currentPath.startsWith('/quran')) {
      return '/quran';
    }
    if (currentPath.includes('/viedma')) {
      return '/app/viedma';
    }
    return '/app';
  };

  const destination = getResolvedDestination();
  const defaultLabel = (isAdmin || location.pathname.startsWith('/admin'))
    ? (isAr ? 'العودة للوحة التحكم' : 'Back to Dashboard')
    : t("not_found.back_home");
  const label = backLabel || defaultLabel;

  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-transparent px-6 transition-all duration-700">
      <div className="w-full max-w-2xl text-center">
        <div className="flex justify-center mb-6">
          <div className="rounded-full bg-red-500/10 p-4 ring-1 ring-red-500/20">
            <FiAlertTriangle className="h-12 w-12 text-red-500" />
          </div>
        </div>
        <p className="text-base font-black text-primary-accent tracking-widest uppercase">404</p>
        <h1 className="mt-4 text-3xl font-black tracking-[-0.04em] text-text-main sm:text-5xl transition-colors duration-700">
          {t("not_found.title")}
        </h1>
        <p className="mt-6 text-base leading-7 text-text-muted transition-colors duration-700">
          {t("not_found.description")}
        </p>
        <div className="mt-10 flex items-center justify-center gap-x-6">
          <Link
            to={destination}
            className="group flex items-center gap-2 rounded-xl bg-text-main dark:bg-primary-accent px-6 py-3 text-sm font-bold text-white dark:text-black transition-all duration-300 hover:shadow-xl hover:opacity-90 active:scale-95"
          >
            <BackIcon className="h-4 w-4 transition-transform group-hover:-translate-x-1 rtl:group-hover:translate-x-1" />
            {label}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
